import type { PopupMessage } from "../types.js";

const POPUP_WIDTH = 480;
const POPUP_HEIGHT = 640;
const POPUP_LOGIN_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const POPUP_CLOSE_POLL_MS = 500;

/** Centers the popup on the current screen (handles multi-monitor offsets). */
function buildPopupFeatures(): string {
  const left = Math.max(0, Math.round((window.screen.width - POPUP_WIDTH) / 2) + window.screenX);
  const top = Math.max(0, Math.round((window.screen.height - POPUP_HEIGHT) / 2) + window.screenY);
  return [
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    "toolbar=0",
    "scrollbars=1",
    "status=0",
    "resizable=1",
    "location=0",
    "menubar=0",
  ].join(",");
}

/**
 * Opens a centered login popup window.
 * Returns `null` when the browser blocks popups.
 */
export function openLoginPopup(url: string): Window | null {
  const popup = window.open(url, "auth_popup", buildPopupFeatures());
  if (popup) {
    try {
      popup.focus();
    } catch {
      // Some browsers throw on cross-origin focus calls — safe to ignore.
    }
  }
  return popup;
}

/**
 * Detects whether the popup was blocked or closed immediately by the browser.
 * Must be called synchronously right after `window.open()`.
 */
export function isPopupBlocked(popup: Window | null): boolean {
  if (!popup) return true;
  try {
    // `popup.closed` should be `false` right after open; if the browser blocked
    // it the reference is often null or `.closed` is already true.
    return popup.closed;
  } catch {
    return true;
  }
}

/**
 * Waits for an `AUTH_SUCCESS`, `AUTH_ERROR`, or `AUTH_CANCEL` message sent
 * by the IdP popup page via `window.postMessage`.
 *
 * Security: only messages whose `event.origin` exactly matches `expectedOrigin`
 * are processed; all others are silently ignored.
 *
 * Resolves with `{ type: "AUTH_CANCEL" }` when the user closes the popup
 * manually without completing authentication.
 *
 * Rejects when `timeoutMs` elapses without a result.
 */
export function waitForPopupMessage(
  expectedOrigin: string,
  popup: Window,
  timeoutMs = POPUP_LOGIN_TIMEOUT_MS
): Promise<PopupMessage> {
  return new Promise<PopupMessage>((resolve, reject) => {
    let settled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      settled = true;
      window.removeEventListener("message", onMessage);
      if (pollTimer !== null) clearInterval(pollTimer);
      if (timeoutTimer !== null) clearTimeout(timeoutTimer);
    };

    const onMessage = (event: MessageEvent) => {
      if (settled) return;
      if (event.origin !== expectedOrigin) return;

      const msg = event.data as Partial<PopupMessage>;
      if (
        msg?.type === "AUTH_SUCCESS" ||
        msg?.type === "AUTH_ERROR" ||
        msg?.type === "AUTH_CANCEL"
      ) {
        cleanup();
        resolve(msg as PopupMessage);
      }
    };

    // Poll for the user closing the popup without completing auth (cancel).
    pollTimer = setInterval(() => {
      if (!settled && popup.closed) {
        cleanup();
        resolve({ type: "AUTH_CANCEL" });
      }
    }, POPUP_CLOSE_POLL_MS);

    timeoutTimer = setTimeout(() => {
      if (!settled) {
        cleanup();
        try {
          popup.close();
        } catch {
          // ignore
        }
        reject(new Error("Popup login timed out. Please try again."));
      }
    }, timeoutMs);

    window.addEventListener("message", onMessage);
  });
}
